import { stringify } from 'qs';

import { api } from 'store/queries';
import {
    pollingAsync,
    resetSearchAsync,
    searchIdAsync,
    setupPagination,
} from 'store/slices/search';
import { searchBoxAsync } from 'store/slices/searchBox';

export const searchApi = api.injectEndpoints({
    endpoints: (build) => ({
        startSearch: build.mutation({
            query: ({
                countryId,
                destinationId,
                resortId,
                propertyId,
                currency,
                term,
                arrival,
                departure,
                rooms,
                occupancy,
            }) => ({
                url: `/search/start?${stringify({
                    c_id: countryId,
                    d_id: destinationId,
                    r_id: resortId,
                    p_id: propertyId,
                    currency,
                    term,
                    arrival,
                    departure,
                    rooms,
                    occupancy,
                })}`,
                method: 'get',
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(resetSearchAsync());
            },
            overrideExisting: true,
        }),
        getSearch: build.query({
            query: (searchId) => ({
                url: `/search/get?${stringify({
                    sid: searchId,
                })}`,
                method: 'get',
            }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(searchIdAsync(data.id));
                dispatch(searchBoxAsync(data));
            },
            overrideExisting: true,
        }),
        getSearchResults: build.query({
            query: ({ searchId, page, sort, filters }) => ({
                url: `/search/results?${stringify({
                    sid: searchId,
                    page,
                    sort,
                    filter: filters,
                })}`,
                method: 'get',
            }),
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg;
            },
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                dispatch(pollingAsync(data.completed));
            },
            overrideExisting: true,
            providesTags: ['Results'],
            invalidatesTags: ['Filters'],
        }),
        getSearchFilters: build.query({
            query: ({ searchId, sort, filters }) => ({
                url: `/search/stats?${stringify({
                    sid: searchId,
                    sort,
                    filter: filters,
                })}`,
                method: 'get',
            }),
            async onQueryStarted(id, { getState, dispatch, queryFulfilled }) {
                const { data } = await queryFulfilled;
                const { search } = getState();

                const firstItem = (search.page - 1) * search.perPage + 1;
                const lastItem = Math.min(
                    firstItem + search.perPage - 1,
                    data.filteredProperties,
                );
                dispatch(setupPagination({ firstItem, lastItem }));
            },
            overrideExisting: true,
            providesTags: ['Filters'],
        }),
        getMapResults: build.query({
            query: ({ searchId, sort, filters }) => ({
                url: `/search/map?${stringify({
                    sid: searchId,
                    sort,
                    filter: filters,
                })}`,
                method: 'get',
            }),
            overrideExisting: true,
        }),
        getPropertySearch: build.query({
            query: ({ searchId, propertyId }) => ({
                url: `/property/search?${stringify({
                    sid: searchId,
                    pid: propertyId,
                })}`,
                method: 'get',
            }),
            overrideExisting: true,
        }),
    }),
});

export const {
    useStartSearchMutation,
    useGetSearchQuery,
    useGetSearchResultsQuery,
    useGetSearchFiltersQuery,
    useGetMapResultsQuery,
    useGetPropertySearchQuery,
} = searchApi;
